import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Bell, AlertCircle, AlertTriangle, AlertOctagon, Info } from "lucide-react";
import { api } from "../../lib/api";

const SEV = {
  critical: { color: "border-[#ff0033] text-[#ff5577] bg-[#ff0033]/10", icon: AlertOctagon, label: "CRITICO" },
  high: { color: "border-amber-500 text-amber-300 bg-amber-500/10", icon: AlertTriangle, label: "ALTO" },
  medium: { color: "border-blue-500/50 text-blue-300 bg-blue-500/10", icon: AlertCircle, label: "MEDIO" },
  low: { color: "border-white/20 text-white/60 bg-white/[0.03]", icon: Info, label: "INFO" },
};

const TIPO_LABEL = {
  documento_auto: "Documento auto",
  documento_scaduto: "Documento SCADUTO",
  rientro_scaduto: "Rientro scaduto",
  saldo_dovuto: "Saldo non incassato",
  manutenzione_imminente: "Manutenzione",
  multa_orfana: "Multa orfana",
};

export default function Alerts() {
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    api.get("/alerts").then((r) => setList(r.data));
  }, []);

  const filtered = filter === "all" ? list : list.filter((a) => a.severita === filter);
  const counts = list.reduce((acc, a) => ({ ...acc, [a.severita]: (acc[a.severita] || 0) + 1 }), {});

  return (
    <div className="space-y-6" data-testid="alerts-page">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-display text-3xl font-black uppercase tracking-tight flex items-center gap-3">
            Alert & Scadenze <Bell className="text-[#ff0033]" />
          </h1>
          <p className="text-sm text-white/45 mt-1">{list.length} alert attivi · documenti, rientri, saldi, manutenzioni, multe</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {["all", "critical", "high", "medium", "low"].map((f) => (
          <button key={f} data-testid={`filter-${f === 'all' ? 'tutti' : f}`} onClick={() => setFilter(f)}
            className={`px-4 py-2 rounded-full text-xs uppercase tracking-widest border transition ${filter === f ? "bg-[#ff0033]/20 text-[#ff5577] border-[#ff0033]/40" : "bg-white/[0.03] border-white/10 text-white/55 hover:border-white/30"}`}>
            {f === "all" ? `Tutti (${list.length})` : `${SEV[f].label} (${counts[f] || 0})`}
          </button>
        ))}
      </div>

      <div className="space-y-3">
        {filtered.map((a, i) => {
          const sev = SEV[a.severita] || SEV.low;
          const Icon = sev.icon;
          return (
            <motion.div key={i} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.02 }}
              className={`glass rounded-2xl p-5 border ${sev.color}`} data-testid={`alert-${i}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex gap-4 min-w-0">
                  <Icon size={22} className={sev.color.split(' ')[1]} />
                  <div className="min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="font-display font-bold">{a.titolo}</span>
                      <span className="text-[10px] uppercase tracking-widest text-white/40">{TIPO_LABEL[a.tipo] || a.tipo}</span>
                    </div>
                    <p className="text-sm text-white/70 mt-1">{a.descrizione}</p>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className={`text-[10px] uppercase tracking-widest font-bold ${sev.color.split(' ')[1]}`}>{sev.label}</div>
                  {a.data && <div className="text-[10px] font-mono text-white/40 mt-1">{a.data}</div>}
                </div>
              </div>
            </motion.div>
          );
        })}
        {filtered.length === 0 && (
          <div className="glass rounded-2xl p-10 text-center text-white/40">
            <Bell size={32} className="mx-auto mb-3 opacity-40" />
            Nessun alert {filter !== "all" ? "in questa categoria" : "attivo"}.
          </div>
        )}
      </div>
    </div>
  );
}
